import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import SourceTrustBadge from '@/components/SourceTrustBadge';
import {
  buildTrustSummary,
  dedupeSourceList,
  getSourceDisplayTitle,
  normalizeSourceTrustLabel,
} from '@/lib/sourceTrust';

/**
 * KnowledgeSourceBadge — shown below assistant chat messages
 * when knowledge_sources are attached.
 *
 * Usage:
 *   <KnowledgeSourceBadge sources={msg.knowledge_sources} />
 */
export default function KnowledgeSourceBadge({ sources }) {
  const [expanded, setExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  const dedupedSources = dedupeSourceList(sources);
  const trustSummary = buildTrustSummary(dedupedSources);
  const summaryText = trustSummary.length > 0
    ? trustSummary
        .slice(0, 3)
        .map((item) => `${item.count} ${item.shortLabel.toLowerCase()}`)
        .join(' · ')
    : 'Matter context attached';

  return (
    <div style={{ marginTop: '8px' }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '999px',
          border: '1px solid color-mix(in srgb, var(--accent-primary) 16%, var(--border-default))',
          background: 'color-mix(in srgb, var(--accent-primary) 7%, var(--bg-elevated))',
          cursor: 'pointer',
          color: 'var(--accent-primary)',
          fontSize: '11px',
          fontFamily: 'var(--font-ui)', fontWeight: 600,
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--accent-primary) 12%, var(--bg-elevated))'}
        onMouseLeave={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--accent-primary) 7%, var(--bg-elevated))'}
      >
        <FileText size={11} />
        Context used ({dedupedSources.length})
        <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
          {summaryText}
        </span>
        {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
      </button>

      {expanded && (
        <div style={{
          marginTop: '8px',
          padding: '10px 12px',
          background: 'var(--bg-elevated)',
          borderRadius: '14px',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          {trustSummary.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {trustSummary.map((item) => (
                <SourceTrustBadge
                  key={item.label}
                  label={item.label}
                  compact
                  count={item.count}
                />
              ))}
            </div>
          )}

          {dedupedSources.map((s, i) => {
            const trustLabel = normalizeSourceTrustLabel(s);
            const detail = [
              s.docsource,
              (s.pageNumber || s.page_number) ? `page ${s.pageNumber || s.page_number}` : null,
              (s.reviewState || s.review_state) ? String(s.reviewState || s.review_state).replace(/_/g, ' ') : null,
            ].filter(Boolean).join(' · ');

            return (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: '9px',
              padding: '3px 0',
            }}>
              <FileText size={11} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                  <span style={{
                    fontSize: '11px',
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-ui)',
                    fontWeight: 600,
                    minWidth: 0,
                  }}>
                    {getSourceDisplayTitle(s)}
                  </span>
                  <SourceTrustBadge label={trustLabel} compact />
                </div>
                {detail && (
                  <div style={{
                    fontSize: '10px',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-ui)',
                  }}>
                    {detail}
                  </div>
                )}
              </div>
              {s.score != null && (
                <span style={{
                  fontSize: '10px', color: 'var(--text-muted)',
                  fontFamily: "'JetBrains Mono', monospace", flexShrink: 0,
                }}>
                  {Math.round(s.score * 100)}%
                </span>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
